import React from "react";
import { normalizeExternalUrl } from "../utils/urlUtils";

/**
 * Social Media Platform Icons
 * Using inline SVG icons for common social media platforms
 */

interface SocialMediaLinksProps {
  twitterUrl?: string;
  facebookUrl?: string;
  instagramUrl?: string;
  linkedinUrl?: string;
  youtubeUrl?: string;
  lineUrl?: string;
  websiteUrl?: string;
  socialMediaOrder?: string[]; // Order to display social media links
  className?: string;
}

interface SocialLink {
  url: string;
  platform: string;
  icon: React.ReactNode;
  ariaLabel: string;
}

/**
 * SocialMediaLinks Component
 *
 * Displays social media icons with links below host display on booking pages
 * Supports: Twitter/X, Facebook, Instagram, LinkedIn, YouTube, LINE, and Website
 *
 * Features:
 * - Responsive grid layout
 * - Opens links in new tab
 * - Hover effects with platform colors
 * - Only renders if at least one link is provided
 */
const SocialMediaLinks: React.FC<SocialMediaLinksProps> = ({
  twitterUrl,
  facebookUrl,
  instagramUrl,
  linkedinUrl,
  youtubeUrl,
  lineUrl,
  websiteUrl,
  socialMediaOrder,
  className = ''
}) => {
  // Build map of available social links
  const availableLinks: Record<string, SocialLink> = {};

  if (twitterUrl) {
    availableLinks.twitter = {
      url: twitterUrl,
      platform: 'twitter',
      ariaLabel: 'X (Twitter)',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
      )
    };
  }

  if (facebookUrl) {
    availableLinks.facebook = {
      url: facebookUrl,
      platform: 'facebook',
      ariaLabel: 'Facebook',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
      )
    };
  }

  if (instagramUrl) {
    availableLinks.instagram = {
      url: instagramUrl,
      platform: 'instagram',
      ariaLabel: 'Instagram',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
        </svg>
      )
    };
  }

  if (linkedinUrl) {
    availableLinks.linkedin = {
      url: linkedinUrl,
      platform: 'linkedin',
      ariaLabel: 'LinkedIn',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
        </svg>
      )
    };
  }

  if (youtubeUrl) {
    availableLinks.youtube = {
      url: youtubeUrl,
      platform: 'youtube',
      ariaLabel: 'YouTube',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
        </svg>
      )
    };
  }

  if (lineUrl) {
    availableLinks.line = {
      url: lineUrl,
      platform: 'line',
      ariaLabel: 'LINE',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.346 0 .627.285.627.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314"/>
        </svg>
      )
    };
  }

  if (websiteUrl) {
    availableLinks.website = {
      url: websiteUrl,
      platform: 'website',
      ariaLabel: 'Website',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
        </svg>
      )
    };
  }

  // Build ordered array based on socialMediaOrder, or use default order
  const socialLinks: SocialLink[] = [];

  if (socialMediaOrder && Array.isArray(socialMediaOrder)) {
    // Use custom order
    socialMediaOrder.forEach(key => {
      if (availableLinks[key]) {
        socialLinks.push(availableLinks[key]);
      }
    });

    // Add any links that weren't in the order array
    Object.keys(availableLinks).forEach(key => {
      if (!socialMediaOrder.includes(key)) {
        socialLinks.push(availableLinks[key]);
      }
    });
  } else {
    // Use default order: twitter, facebook, instagram, linkedin, youtube, line, website
    const defaultOrder = ['twitter', 'facebook', 'instagram', 'linkedin', 'youtube', 'line', 'website'];
    defaultOrder.forEach(key => {
      if (availableLinks[key]) {
        socialLinks.push(availableLinks[key]);
      }
    });
  }

  // Don't render if no social links
  if (socialLinks.length === 0) {
    return null;
  }

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`}>
      {socialLinks.map((link) => (
        <a
          key={link.platform}
          href={normalizeExternalUrl(link.url)}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={link.ariaLabel}
          className="
            p-2 rounded-lg
            text-gray-600
            transition-all duration-200
            social-media-icon
            focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-gray-400
          "
          title={link.ariaLabel}
        >
          {link.icon}
        </a>
      ))}
    </div>
  );
};

export default SocialMediaLinks;
